import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Dropdown, Modal, OverlayTrigger, Row, Tooltip} from 'react-bootstrap';
import {AgencyContractModalLeftSide} from './components/AgencyContractModalLeftSide';
import {AgencyContractModalRightSide} from './components/AgencyContractModalRightSide';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import { IAllStates } from '../../../store/rootReducer';
import {IDeclineOffer} from '../../influencer/offer/dto/IDeclineOffer';
import {priceSplitter} from '../../../utils/helpers/priceSplitter';
import {formatDate, getGrandTotal, getNumberContentPieces, getStatusIcon} from '../../../shared/functions/Functions';
import {AgencyContractService, AgencyService} from '../service';
import {
    setActiveTab,
    setCampaignId, setInitialState,
    setOfferModalDetails,
    setContractModalStatus,
} from '../../../store/agency/agencyContractSlice';
import {ErrorToast, SuccessToast} from '../../../utils/toasters';
import {gtmService} from '../../../utils/gtm.service';
import {setUpdateInfluencerList} from '../../../store/agency/agencySlice';
import {scrollToTopRef} from '../../../utils/helpers/ScrollToTop';
import {scrollToBottomRef} from '../../../utils/helpers/scrollToBottomRef';
import {CustomImageHandler} from '../../../shared/components/CustomImageHandler';
import {FiInfo} from 'react-icons/fi';
import HtmlToPdf from '../../../shared/components/generate-pdf';
import {
    InfluencerAcceptOfferModal,
} from '../../influencer/offer/modal/offer-modal/components/InfluencerAcceptOfferModal';
import {DeclineOfferModal} from '../../influencer/offer/modal/offer-modal/components/DeclineOfferModal';
import {ConfirmSendingOfferModal} from '../../influencer/offer/modal/offer-modal/components/ConfirmSendingOfferModal';

interface IAgencyInfluencerContractModalProps {
    show: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    updateList?: React.Dispatch<React.SetStateAction<boolean>>;
    campaignId: string;
    userId?: number;
}

export function AgencyInfluencerContractModal({
                                               setShowModal,
                                               show,
                                               campaignId,
                                               updateList,
                                           }: IAgencyInfluencerContractModalProps) {
    const {
        campaign,
        activeTabModal,
        initialContract,
        isEditable,
        canCounter,
        contractModalStatus,
        hasLegal,
        selectedMatchedUser,
        isCharity, isLegalChecked,
        isDistributionChecked,
        requiredFieldChangeColor,
    } = useSelector((state: IAllStates) => state.contractAgency);
    const {selectedInfluencer} = useSelector((reduxState: IAllStates) => reduxState.agency);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({declineReason: '', declineComment: ''});
    const offerModalRef = useRef<HTMLDivElement>(null);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const grandTotalSum = priceSplitter(getGrandTotal(initialContract.contractItems, initialContract.distribution));

    useEffect(() => {
        (show && selectedMatchedUser?.id) && AgencyContractService.getAgencyContractModalDetail(campaignId, selectedMatchedUser?.id)
            .then((response) => {
                dispatch(setOfferModalDetails(response));
                dispatch(setCampaignId(campaignId));
            }).catch((err) => {
                closeModal();
                ErrorToast(err);
            });
    }, [show, selectedMatchedUser]);

    const sendInitialOfferRequest = () => {
        setDisableButton(true);
        let initialContractCopy = initialContract;
        const contractItems = isCharity ? initialContractCopy.contractItems : initialContractCopy.contractItems.filter(item => (item.nbPosts > 0 && item.price > 0));
        const price = getGrandTotal(initialContractCopy.contractItems, initialContractCopy.distribution);
        initialContractCopy = {...initialContractCopy, contractItems, price};
        AgencyContractService.sendInitialAgencyContract(campaignId, initialContractCopy, selectedMatchedUser?.id)
            .then((response) => {
                gtmService('influencer_offer_submitted', grandTotalSum);
                SuccessToast(t('influencer.offer_management.modal.success_initial_message'));
                setDisableButton(false);
                if (updateList) {
                    updateList((prevState => !prevState));
                }
                dispatch(setUpdateInfluencerList({}));
                closeModal();
            })
            .catch(err => {
                ErrorToast(err);
                setDisableButton(false);
            });
    };

    const sendCounterOfferRequest = () => {
        setDisableButton(true);
        let initialContractCopy = initialContract;
        const contractItems = isCharity ? initialContractCopy.contractItems : initialContractCopy.contractItems.filter(item => (item.nbPosts > 0 && item.price > 0));
        const price = getGrandTotal(initialContractCopy.contractItems, initialContractCopy.distribution);
        initialContractCopy = {...initialContractCopy, contractItems, price};
        AgencyContractService.sendAgencyCounterContract(campaignId, initialContractCopy, selectedMatchedUser?.id)
            .then((response) => {
                SuccessToast(t('influencer.offer_management.modal.success_update_message'));
                if (updateList) {
                    updateList((prevState => !prevState));
                }
                dispatch(setUpdateInfluencerList({}));
                gtmService('influencer_offer_submitted', grandTotalSum);
                setDisableButton(false);
                closeModal();
            })
            .catch(err => {
                ErrorToast(err);
                setDisableButton(false);
            });
    };

    const acceptContract = () => {
        AgencyContractService.acceptAgencyContract(campaignId, selectedMatchedUser?.id)
            .then((response) => {
                if (updateList) {
                    gtmService('influencer_offer_accepted');
                    updateList((prevState => !prevState));
                    // setIsRocketShown && setIsRocketShown(true);
                }
                dispatch(setUpdateInfluencerList({}));
                SuccessToast(t('influencer.offer_management.modal.success_accepted_message'));
                closeModal();
            }).catch(err => ErrorToast(err));
    };

    const withdrawContract = () => {
        AgencyContractService.withdrawAgencyContract(campaignId, selectedMatchedUser?.id)
            .then((response) => {
                if (updateList) {
                    updateList((prevState => !prevState));
                }
                dispatch(setUpdateInfluencerList({}));
                SuccessToast(t('influencer.offer_management.modal.success_withdraw_message'));
                closeModal();
            }).catch(err => ErrorToast(err));
    };
    const declineContract = () => {
        AgencyContractService.declineAgencyContract(campaignId, declineReasons, selectedMatchedUser?.id)
            .then((response) => {
                if (updateList) {
                    updateList((prevState => !prevState));
                }
                dispatch(setUpdateInfluencerList({}));
                SuccessToast(t('influencer.offer_management.modal.success_decline_message'));
                closeModal();
            }).catch(err => ErrorToast(err));
    };
    const changeStatus = () => {
        dispatch(setActiveTab(4));
        dispatch(setContractModalStatus('negotiate'));
    };
    const closeAcceptModal = () => {
        setShowAcceptModal(false);
    };
    const closeSubmitModal = () => {
        setShowSubmitModal(false);
    };
    const closeDeclineModal = () => {
        setShowDeclineModal(false);
    };

    const closeModal = () => {
        dispatch(setInitialState({}));
        setShowModal(false);
    };

    const openSubmitConfirmModal = (event: any) => {
        event.preventDefault();
        setShowSubmitModal(true);
    };

    const setNextTab = () => {
        let currentStep;
        if (!hasLegal && activeTabModal === 2) {
            currentStep = activeTabModal + 2;
        } else {
            currentStep = activeTabModal + 1;
        }
        if (requiredFieldChangeColor && activeTabModal === 1) {
            scrollToTopRef(offerModalRef);
        } else if (!isLegalChecked && activeTabModal === 3) {
            scrollToBottomRef(offerModalRef);
        } else if (!isDistributionChecked && activeTabModal === 2) {
            scrollToBottomRef(offerModalRef);
        } else {
            scrollToTopRef(offerModalRef);
        }
        dispatch(setActiveTab(currentStep));
    };

    return (
        <>
            <Modal show={show}
                   onHide={closeModal}
                   size="xl"
                   id="offer-modal"
                   aria-labelledby="contained-modal-title-vcenter"
                   centered
            >
                <form
                    onSubmit={(e) => openSubmitConfirmModal(e)}>
                    <Modal.Body
                        className={`${canCounter || (campaign?.contracts[0]?.status === 'negotiate') ? 'negotiate-status-mobile' : `border border-2 border-${campaign?.contracts[0]?.status}-color-mobile border-bottom-0`}`}>
                        {campaign?.contracts[0]?.id ?
                            <div className={`offer-status-badge ${campaign?.contracts[0]?.status} d-none d-md-flex`}>
                                {getStatusIcon(campaign?.contracts[0]?.status ?? '')}
                                <p>{t(`general.offerStatus.${campaign?.contracts[0]?.status}`)}</p>
                            </div> : null
                        }
                        <Row className="pb-4">
                            <Col xl={3} lg={12} className="profile-info px-0 px-md-3">
                                <AgencyContractModalLeftSide campaignId={campaignId}/>
                            </Col>
                            <Col xl={9} lg={12} className="px-0 px-md-3">
                                <AgencyContractModalRightSide contractModalRef={offerModalRef}/>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="position-relative bg-white shadow-top">
                        <Row className="justify-content-between align-items-center">
                            <Col xl={6} className="col-auto d-flex justify-content-md-between justify-content-end">
                                <div className="d-md-flex d-none align-items-center">
                                    <CustomImageHandler photoPath={campaign?.company?.profilePhoto?.path ?? ''}
                                                        altTag="User Profile"
                                                        classes="middle-rounded-logo"/>
                                    <h6 className="mb-0 ml-2">{campaign?.company?.displayName}</h6>
                                </div>
                                {activeTabModal === 4 ?
                                    <div className="d-block text-uppercase d-md-none mb-2">
                                        <div className="d-flex align-items-top">
                                            {campaign?.isCharity ? (
                                                <>
                                                    {+grandTotalSum > 0 ? (
                                                        <h5 className="text-primary mb-0 euro-symbol">
                                                            {grandTotalSum}
                                                        </h5>
                                                    ) : (
                                                        <h5 className="text-primary-dark m-0 font-14">{t('general.button.barter_deal')}</h5>
                                                    )}
                                                </>
                                            ) : (
                                                <h5 className="text-primary mb-0 euro-symbol">
                                                    {grandTotalSum}
                                                </h5>
                                            )}
                                            {isCharity && (
                                                <OverlayTrigger placement="top"
                                                                overlay={<Tooltip
                                                                    id="impressions-tooltip">
                                                                    {t(`influencer.explore.barterTooltip`)}
                                                                </Tooltip>}>
                                                    <FiInfo size={14} className="cursor-pointer ml-2"/>
                                                </OverlayTrigger>
                                            )}
                                        </div>
                                        <h6 className="mb-0 mt-1 font-11">{t('brand.campaign.book.offer.modal.grand_total')}</h6>
                                    </div> : null
                                }
                            </Col>
                            <Col md={12} xl={6}
                                 className="d-flex align-items-center justify-content-between justify-content-md-end col-auto">
                                {(campaign?.contracts[0]?.status === 'accepted' || campaign?.contracts[0]?.status === 'completed') && (
                                    <HtmlToPdf styleContent="mr-4" passedOffers={campaign?.contracts[0].id}/>
                                )}
                                {activeTabModal === 4 &&
                                    <div className="align-items-center mr-md-4 mr-2 d-none d-md-flex text-uppercase">
                                        {isCharity && (
                                            <OverlayTrigger placement="top"
                                                            overlay={<Tooltip
                                                                id="impressions-tooltip">
                                                                {t(`influencer.explore.barterTooltip`)}
                                                            </Tooltip>}>
                                                <FiInfo size={14} className="cursor-pointer ml-2"/>
                                            </OverlayTrigger>
                                        )}
                                        <h6 className="mb-0 mx-1">{t('brand.campaign.book.offer.modal.grand_total')}:</h6>
                                        <div className="d-flex align-items-top">
                                            {campaign?.isCharity ? (
                                                <>
                                                    {+grandTotalSum > 0 ? (
                                                        <h5 className="text-primary mb-0 euro-symbol">
                                                            {grandTotalSum}
                                                        </h5>
                                                    ) : (
                                                        <h5 className="text-primary-dark m-0 font-14">{t('general.button.barter_deal')}</h5>
                                                    )}
                                                </>
                                            ) : (
                                                <h5 className="text-primary mb-0 euro-symbol">
                                                    {grandTotalSum}
                                                </h5>
                                            )}
                                        </div>
                                    </div>
                                }
                                <Button onClick={closeModal} className="mr-3 text-uppercase" variant="outline-secondary"
                                        size="sm">
                                    {t('general.button.close')}
                                </Button>
                                <div className="d-flex">
                                    {((!campaign?.contractPlaceholder?.flags?.canWithdraw && !canCounter) || contractModalStatus === 'negotiate') ? null :
                                        <Dropdown>
                                            <Dropdown.Toggle className="mr-2 btn-outline-primary btn-sm" variant="light"
                                                             id="dropdown-basic">
                                                {t('general.button.respond')}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {isEditable &&
                                                    <Dropdown.Item id="negotiate"
                                                                   onClick={changeStatus}> {t('general.button.negotiate')} </Dropdown.Item>
                                                }
                                                {canCounter &&
                                                    <Dropdown.Item id="decline"
                                                                   onClick={() => setShowDeclineModal(true)}>
                                                        {t('general.button.decline')}
                                                    </Dropdown.Item>
                                                }
                                                {campaign?.contractPlaceholder?.flags?.canWithdraw &&
                                                    <Dropdown.Item id="withdraw" onClick={withdrawContract}>
                                                        {t('brand.campaign.book.offer.modal.withdraw_offer')}
                                                    </Dropdown.Item>
                                                }
                                            </Dropdown.Menu>
                                        </Dropdown>}
                                    <Button onClick={setNextTab} className={`${activeTabModal >= 4 ? 'd-none' : ''}`}
                                            variant="primary" size="sm">
                                        {t('general.button.next')}
                                    </Button>
                                    <Button onSubmit={(e) => openSubmitConfirmModal(e)}
                                            type="submit"
                                            className={`${(campaign?.contracts?.length || activeTabModal !== 4) ? 'd-none' : ''}`}
                                            variant={isCharity ? 'primary-dark' : 'primary'} size="sm">
                                        {t('general.button.submit_contract')}
                                    </Button>
                                    <Button type="submit"
                                            className={`${(canCounter && isEditable && activeTabModal === 4 && contractModalStatus === 'negotiate') ? '' : 'd-none'}`}
                                            variant="primary" size="sm">
                                        {t('general.button.submit')}
                                    </Button>
                                    <Button
                                        className={`${(canCounter && activeTabModal === 4 && contractModalStatus === 'preview' && !campaign?.contracts[0]?.isRequested) ? '' : 'd-none'} ml-2`}
                                        variant="primary" size="sm"
                                        onClick={() => setShowAcceptModal(true)}
                                    >
                                        {t('general.button.accept')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Footer>
                </form>
                <InfluencerAcceptOfferModal
                    show={showAcceptModal}
                    closeModal={closeAcceptModal}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    grandTotal={grandTotalSum}
                    action={acceptContract}
                />
                <DeclineOfferModal isContract={true}
                                   date={formatDate(campaign?.contracts[0]?.createdAt ?? '')} declineOffer={declineContract}
                                   show={showDeclineModal} closeModal={closeDeclineModal}
                                   setDeclineReason={setDeclineReasons}/>
                <ConfirmSendingOfferModal
                    action={campaign?.contracts?.length ? sendCounterOfferRequest : sendInitialOfferRequest}
                    grandTotal={grandTotalSum}
                    disableButton={disableButton}
                    show={showSubmitModal}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    isInfluencer={true}
                    closeModal={closeSubmitModal}/>
            </Modal>
        </>
    );
}
